import { useState, useMemo } from 'react';
import './SubvencionsCalendar.css';

const MONTHS = [
    'Gener', 'Febrer', 'Març', 'Abril', 'Maig', 'Juny',
    'Juliol', 'Agost', 'Setembre', 'Octubre', 'Novembre', 'Desembre'
];

const DAYS = ['Dl', 'Dt', 'Dc', 'Dj', 'Dv', 'Ds', 'Dg'];

function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameDay(d1, d2) {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
}

function getCalendarWeeks(year, month) {
    const firstOfMonth = new Date(year, month, 1);
    let startDow = firstOfMonth.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();

    const cells = [];

    for (let i = startDow - 1; i >= 0; i--) {
        cells.push({
            date: new Date(year, month - 1, prevMonthDays - i),
            dayOfMonth: prevMonthDays - i,
            isCurrentMonth: false,
        });
    }

    for (let d = 1; d <= daysInMonth; d++) {
        cells.push({
            date: new Date(year, month, d),
            dayOfMonth: d,
            isCurrentMonth: true,
        });
    }

    while (cells.length % 7 !== 0) {
        const d = cells.length - startDow - daysInMonth + 1;
        cells.push({
            date: new Date(year, month + 1, d),
            dayOfMonth: d,
            isCurrentMonth: false,
        });
    }

    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
}

function computeBarsForWeek(grants, weekCells) {
    const weekStartDay = startOfDay(weekCells[0].date);
    const weekEndDay = startOfDay(weekCells[6].date);

    const bars = [];

    grants.forEach(grant => {
        if (!grant.openAt || !grant.closeAt) return;

        const grantOpen = startOfDay(new Date(grant.openAt));
        const grantClose = startOfDay(new Date(grant.closeAt));

        if (grantOpen > weekEndDay || grantClose < weekStartDay) return;

        let startCol = 0;
        if (grantOpen > weekStartDay) {
            for (let i = 0; i < 7; i++) {
                const cellDay = startOfDay(weekCells[i].date);
                if (cellDay >= grantOpen) {
                    startCol = i;
                    break;
                }
            }
        }

        let endCol = 6;
        if (grantClose < weekEndDay) {
            for (let i = 6; i >= 0; i--) {
                const cellDay = startOfDay(weekCells[i].date);
                if (cellDay <= grantClose) {
                    endCol = i;
                    break;
                }
            }
        }

        const isRangeStart = grantOpen >= weekStartDay && grantOpen <= weekEndDay;
        const isRangeEnd = grantClose >= weekStartDay && grantClose <= weekEndDay;

        bars.push({
            grant,
            startCol,
            endCol,
            isRangeStart,
            isRangeEnd,
        });
    });

    return bars;
}

function SubvencionsCalendar({ grants, grantColors, selectedGrant, onSelectGrant }) {
    const today = useMemo(() => new Date(), []);
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth());

    const weeks = useMemo(() => getCalendarWeeks(year, month), [year, month]);

    const sortedGrants = useMemo(() => {
        const statusOrder = { open: 0, pending: 1, closed: 2, unknown: 3 };
        return [...grants].sort((a, b) => {
            const s = (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
            if (s !== 0) return s;
            return new Date(a.openAt) - new Date(b.openAt);
        });
    }, [grants]);

    const grantOrder = useMemo(() => {
        const map = {};
        sortedGrants.forEach((g, i) => { map[g.slug] = i; });
        return map;
    }, [sortedGrants]);

    const changeMonth = (delta) => {
        let newMonth = month + delta;
        let newYear = year;
        if (newMonth < 0) { newMonth = 11; newYear--; }
        if (newMonth > 11) { newMonth = 0; newYear++; }
        setMonth(newMonth);
        setYear(newYear);
    };

    const goToToday = () => {
        setMonth(today.getMonth());
        setYear(today.getFullYear());
    };

    return (
        <div className="subvencions-cal-container">
            <div className="subvencions-cal-header">
                <button
                    className="subvencions-cal-nav-btn"
                    onClick={() => changeMonth(-1)}
                >
                    ←
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="subvencions-cal-month-year">
                        {MONTHS[month]} {year}
                    </span>
                    <button
                        className="subvencions-cal-today-btn"
                        onClick={goToToday}
                    >
                        Avui
                    </button>
                </div>
                <button
                    className="subvencions-cal-nav-btn"
                    onClick={() => changeMonth(1)}
                >
                    →
                </button>
            </div>

            <div className="subvencions-cal-days-header">
                {DAYS.map(d => (
                    <div key={d} className="subvencions-cal-day-name">{d}</div>
                ))}
            </div>

            {weeks.map((week, wi) => {
                const bars = computeBarsForWeek(sortedGrants, week);
                const packedBars = bars
                    .sort((a, b) => (grantOrder[a.grant.slug] ?? 0) - (grantOrder[b.grant.slug] ?? 0))
                    .map((bar, i) => ({ ...bar, row: i + 1 }));

                return (
                    <div key={wi} className="subvencions-cal-week">
                        <div className="subvencions-cal-days">
                            {week.map((cell, di) => {
                                const isDayToday = isSameDay(cell.date, today);
                                const classes = [
                                    'subvencions-cal-day',
                                    !cell.isCurrentMonth ? 'subvencions-day-other-month' : '',
                                    isDayToday ? 'subvencions-day-today' : '',
                                ].filter(Boolean).join(' ');

                                return (
                                    <div key={di} className={classes}>
                                        {cell.dayOfMonth}
                                    </div>
                                );
                            })}
                        </div>
                        {packedBars.length > 0 ? (
                            <div className="subvencions-cal-bars">
                                {packedBars.map(bar => {
                                    const isClosed = bar.grant.status === 'closed';
                                    const barColor = grantColors[bar.grant.slug] || '#9e9e9e';
                                    const classes = [
                                        'subvencions-cal-bar',
                                        bar.isRangeStart ? 'subvencions-bar-range-start' : '',
                                        bar.isRangeEnd ? 'subvencions-bar-range-end' : '',
                                        selectedGrant?.slug === bar.grant.slug ? 'subvencions-bar-selected' : '',
                                    ].filter(Boolean).join(' ');

                                    return (
                                        <div
                                            key={bar.grant.slug}
                                            className={classes}
                                            style={{
                                                gridColumn: `${bar.startCol + 1} / ${bar.endCol + 2}`,
                                                gridRow: bar.row,
                                                backgroundColor: barColor,
                                                opacity: isClosed ? 0.45 : 1,
                                            }}
                                            onClick={() => onSelectGrant(bar.grant)}
                                            title={bar.grant.title}
                                        >
                                            {bar.isRangeStart ? bar.grant.title : ''}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

export default SubvencionsCalendar;
